import { Directive, ElementRef, Input, type OnChanges, Renderer2, inject } from '@angular/core';
import {
    DEFAULT_BACKGROUND_DERIVATION,
    DEFAULT_COLOR_DERIVATION,
    generateBackgroundColorClass,
    generateColoredBackgroundColorClass,
    generateColoredTextColorClass,
    generateTextOnColoredBackgroundColorClass
} from './shared-ui-color-classes.utils';
import type {
    SharedUiBackgroundColorDerivation,
    SharedUiBackgroundColorName,
    SharedUiColorName,
    SharedUiColoredBackgroundColorDerivation
} from './shared-ui-colors.types';

@Directive({ selector: '[coloredTextColor]' })
export class SharedUiColoredTextColorDirective implements OnChanges {
    private _renderer = inject(Renderer2);
    private _elementRef = inject(ElementRef);

    private _previousClass?: string;

    @Input() coloredTextColor?: SharedUiColorName | null;

    ngOnChanges() {
        if (this._previousClass) {
            this._renderer.removeClass(this._elementRef.nativeElement, this._previousClass);
            this._previousClass = undefined;
        }

        if (this.coloredTextColor) {
            const newClass = generateColoredTextColorClass(this.coloredTextColor);

            this._renderer.addClass(this._elementRef.nativeElement, newClass);
            this._previousClass = newClass;
        }
    }
}

@Directive({ selector: '[coloredBackgroundColor]' })
export class SharedUiColoredBackgroundColorDirective implements OnChanges {
    private _renderer = inject(Renderer2);
    private _elementRef = inject(ElementRef);

    private _previousClass?: string;

    @Input() coloredBackgroundColor!: SharedUiColorName;
    @Input() coloredBackgroundColorDerivation: SharedUiColoredBackgroundColorDerivation = DEFAULT_COLOR_DERIVATION;

    ngOnChanges() {
        if (this._previousClass) {
            this._renderer.removeClass(this._elementRef.nativeElement, this._previousClass);
        }

        if (this.coloredBackgroundColor) {
            const newClass = generateColoredBackgroundColorClass({
                color: this.coloredBackgroundColor,
                derivation: this.coloredBackgroundColorDerivation
            });

            this._renderer.addClass(this._elementRef.nativeElement, newClass);
            this._previousClass = newClass;
        }
    }
}

@Directive({ selector: '[coloredTextOnColoredBackground]' })
export class SharedUiColoredTextColoredOnColoredBackgroundDirective implements OnChanges {
    private _renderer = inject(Renderer2);
    private _elementRef = inject(ElementRef);

    private _previousClass?: string;

    @Input() coloredTextOnColoredBackground!: SharedUiColorName;
    @Input() colorDerivation: SharedUiColoredBackgroundColorDerivation = DEFAULT_COLOR_DERIVATION;

    ngOnChanges() {
        if (this._previousClass) {
            this._renderer.removeClass(this._elementRef.nativeElement, this._previousClass);
        }
        const newClass = generateTextOnColoredBackgroundColorClass({
            color: this.coloredTextOnColoredBackground,
            derivation: this.colorDerivation
        });

        this._renderer.addClass(this._elementRef.nativeElement, newClass);
        this._previousClass = newClass;
    }
}

@Directive({ selector: '[backgroundColor]' })
export class SharedUiBackgroundColorDirective implements OnChanges {
    private _renderer = inject(Renderer2);
    private _elementRef = inject(ElementRef);

    private _previousClass?: string;

    @Input() backgroundColor!: SharedUiBackgroundColorName;
    @Input() backgroundColorDerivation?: SharedUiBackgroundColorDerivation;

    ngOnChanges() {
        if (this._previousClass) {
            this._renderer.removeClass(this._elementRef.nativeElement, this._previousClass);
        }
        const newClass = generateBackgroundColorClass({
            color: this.backgroundColor,
            derivation: this.backgroundColorDerivation ?? DEFAULT_BACKGROUND_DERIVATION
        });
        this._renderer.addClass(this._elementRef.nativeElement, newClass);
        this._previousClass = newClass;
    }
}
